import type { TimeSlotMenu, ProcessedSlot } from '../types/timeSlots';
import type { SelectedPairing } from '../types/pairings';
import { shouldBypassGuarantee } from './guaranteeBypass';

export interface FeeBreakdown {
  guaranteeType: 'deposit' | 'no_show' | null;
  guaranteeSource: 'menu' | 'slot' | 'specialRequest' | 'bypass' | null;
  baseFee: number;
  perPerson: boolean;
  gratuity: number;
  gratuityPercent: number;
  menuTax: number;
  salesTax: number;
  salesTaxPercent: number;
  serviceFee: number;
  serviceFeePercent: number;
  pairingsFee: number;
  total: number;
  /** true when gratuity or tax or serviceFee > 0 (controls showing detail rows) */
  hasBreakdown: boolean;
  currency: string;
  isUSD: boolean;
}

export interface CalculateFeeParams {
  slot: ProcessedSlot;
  menu: TimeSlotMenu | undefined;
  partySize: number;
  kidsCount: number;
  settleUsd: boolean;
  salesTaxPercent: number;
  restaurantSubdomain?: string;
  checkoutProcessingFeePercent?: number;
  tipChargePercent?: number;
  isForeign?: boolean;
  hasGiftCard?: boolean;
  rescheduleOriginalType?: string | null;
  hasMatchingSpecialRequest?: boolean;
  selectedPairings?: SelectedPairing[];
  isAgent?: boolean;
  guaranteeCurrency?: string;
  restaurantCurrency?: string;
}

const DEFAULT_GRATUITY_PERCENT = 12;
const QUINTONIL_GRATUITY_PERCENT = 15;

export const AGENT_ROLES = ['ADMIN', 'RESTAURATEUR', 'FOTH', 'AGENT', 'SUPER_AGENT'];

/** Roles that bypass guarantee */
export function isAgentRole(role: string | null | undefined): boolean {
  if (!role) return false;
  const normalized = role.replace(/^ROLE_/i, '').toUpperCase();
  return AGENT_ROLES.includes(normalized);
}

function makeBypassed(currency: string, isUSD: boolean): FeeBreakdown {
  return {
    guaranteeType: null,
    guaranteeSource: 'bypass',
    baseFee: 0,
    perPerson: false,
    gratuity: 0,
    gratuityPercent: 0,
    menuTax: 0,
    salesTax: 0,
    salesTaxPercent: 0,
    serviceFee: 0,
    serviceFeePercent: 0,
    pairingsFee: 0,
    total: 0,
    hasBreakdown: false,
    currency,
    isUSD,
  };
}

/**
 * Calculate guarantee fee following the legacy 4-level hierarchy:
 *
 * 1. Bypass checks (gift card, agent, !allowGuarantee)
 * 2. Menu guarantee (if menu.guarantee.type in [deposit, no_show])
 * 3. Slot guarantee (if slot.type in [deposit, no_show])
 * 4. specialRequestGuaranties (if slot has it and user has matching personalization)
 *
 * Fee composition (after base fee):
 *   subtotal = baseFee
 *   gratuity = baseFee × (tipChargePercent / 100) [deposit + chargeTip only]
 *   menuTax  = menu.tax × (perPerson ? partySize : 1) [deposit only]
 *   salesTax = (subtotal + gratuity + menuTax) × (processingSalesTax / 100)
 *   serviceFee = (subtotal + gratuity + menuTax + salesTax) × (checkoutProcessingFeePercent / 100) [deposit only]
 *   total = subtotal + gratuity + menuTax + salesTax + serviceFee
 */
export function calculateGuaranteeFee({
  slot,
  menu,
  partySize,
  kidsCount,
  settleUsd,
  salesTaxPercent,
  restaurantSubdomain,
  checkoutProcessingFeePercent = 0,
  tipChargePercent,
  isForeign = false,
  hasGiftCard = false,
  rescheduleOriginalType,
  hasMatchingSpecialRequest = false,
  selectedPairings = [],
  isAgent = false,
  guaranteeCurrency,
  restaurantCurrency,
}: CalculateFeeParams): FeeBreakdown {
  const currency = guaranteeCurrency ?? restaurantCurrency ?? 'USD';
  const pairingsInUsd = pairingsPricedInUsd(settleUsd, restaurantCurrency);
  const isUSD = settleUsd;

  // ─── Level 1: Bypass checks ────────────────────────────────────────
  if (isAgent) {
    return makeBypassed(currency, isUSD);
  }

  // Gift card or reschedule of a guaranteed reservation
  if (shouldBypassGuarantee({ hasGiftCard, rescheduleOriginalType })) {
    return makeBypassed(currency, isUSD);
  }

  if (menu && !menu.allowGuarantee) {
    return makeBypassed(currency, isUSD);
  }

  // ─── Level 2: Menu guarantee ───────────────────────────────────────
  const guarantee = menu?.guarantee;
  const requireMenuGuarantee =
    menu?.allowGuarantee &&
    guarantee &&
    (guarantee.type === 'deposit' || guarantee.type === 'no_show');

  if (requireMenuGuarantee) {
    return computeFeeFromMenu({
      guarantee: guarantee!,
      menu: menu!,
      slot,
      partySize,
      kidsCount,
      settleUsd,
      salesTaxPercent,
      restaurantSubdomain,
      checkoutProcessingFeePercent,
      tipChargePercent,
      isForeign,
      selectedPairings,
      pairingsInUsd,
      currency,
      isUSD,
    });
  }

  // ─── Level 3: Slot guarantee ───────────────────────────────────────
  const requireSlotGuarantee =
    slot.type === 'deposit' || slot.type === 'no_show';

  if (requireSlotGuarantee) {
    return computeFeeFromSlot({
      slot,
      menu,
      partySize,
      kidsCount,
      settleUsd,
      salesTaxPercent,
      restaurantSubdomain,
      checkoutProcessingFeePercent,
      tipChargePercent,
      isForeign,
      selectedPairings,
      pairingsInUsd,
      currency,
      isUSD,
    });
  }

  // ─── Level 4: specialRequestGuaranties ─────────────────────────────
  if (
    slot.specialRequestGuaranties &&
    hasMatchingSpecialRequest &&
    slot.specialRequestGuarantiesType &&
    (slot.specialRequestGuarantiesType === 'deposit' || slot.specialRequestGuarantiesType === 'no_show')
  ) {
    const gType = slot.specialRequestGuarantiesType as 'deposit' | 'no_show';
    const baseFee = slot.specialRequestGuarantiesAmount ?? 0;

    return composeFee({
      guaranteeType: gType,
      guaranteeSource: 'specialRequest',
      baseFee,
      perPerson: false,
      menu,
      partySize,
      salesTaxPercent,
      checkoutProcessingFeePercent,
      tipChargePercent,
      restaurantSubdomain,
      pairingsFee: 0,
      currency,
      isUSD,
    });
  }

  // ─── Default: traditional ─────────────────────────────────────────
  return {
    guaranteeType: null,
    guaranteeSource: null,
    baseFee: 0,
    perPerson: false,
    gratuity: 0,
    gratuityPercent: 0,
    menuTax: 0,
    salesTax: 0,
    salesTaxPercent: 0,
    serviceFee: 0,
    serviceFeePercent: 0,
    pairingsFee: 0,
    total: 0,
    hasBreakdown: false,
    currency,
    isUSD,
  };
}

// ─── Menu guarantee computation ───────────────────────────────────────

interface MenuFeeParams {
  guarantee: NonNullable<TimeSlotMenu['guarantee']>;
  menu: TimeSlotMenu;
  slot: ProcessedSlot;
  partySize: number;
  kidsCount: number;
  settleUsd: boolean;
  salesTaxPercent: number;
  restaurantSubdomain?: string;
  checkoutProcessingFeePercent: number;
  tipChargePercent?: number;
  isForeign: boolean;
  selectedPairings: SelectedPairing[];
  pairingsInUsd: boolean;
  currency: string;
  isUSD: boolean;
}

function computeFeeFromMenu({
  guarantee,
  menu,
  slot,
  partySize,
  kidsCount,
  settleUsd,
  salesTaxPercent,
  restaurantSubdomain,
  checkoutProcessingFeePercent,
  tipChargePercent,
  isForeign,
  selectedPairings,
  pairingsInUsd,
  currency,
  isUSD,
}: MenuFeeParams): FeeBreakdown {
  let gType = guarantee.type as 'deposit' | 'no_show';
  let unitFee: number;
  let kidUnitFee: number;

  // Check foreign/local special rules
  if (isForeign && menu.specialRuleForForeign) {
    gType = menu.specialRuleForForeign.type as 'deposit' | 'no_show';
    unitFee = settleUsd
      ? (menu.specialRuleForForeign.feeUSD || menu.specialRuleForForeign.fee)
      : menu.specialRuleForForeign.fee;
    kidUnitFee = 0; // special rules don't have kid fees
  } else if (!isForeign && menu.specialRuleForLocals) {
    gType = menu.specialRuleForLocals.type as 'deposit' | 'no_show';
    unitFee = settleUsd
      ? (menu.specialRuleForLocals.feeUSD || menu.specialRuleForLocals.fee)
      : menu.specialRuleForLocals.fee;
    kidUnitFee = 0;
  } else {
    unitFee = settleUsd
      ? (guarantee.feeUSD || guarantee.fee || 0)
      : (guarantee.fee ?? 0);
    kidUnitFee = settleUsd
      ? (guarantee.feeKidsUSD || guarantee.feeKids || 0)
      : (guarantee.feeKids ?? 0);
  }

  // Per-person calculation
  const perPerson = !!guarantee.perPerson;
  let baseFee: number;

  if (perPerson) {
    if (kidUnitFee > 0 && kidsCount > 0) {
      const adultsCount = Math.max(partySize - kidsCount, 0);
      baseFee = (unitFee * adultsCount) + (kidUnitFee * kidsCount);
    } else {
      baseFee = unitFee * partySize;
    }
  } else {
    baseFee = unitFee;
  }

  // Add pairing guarantee
  const pairingsFee = calculatePairingsFee(selectedPairings, pairingsInUsd);
  baseFee += pairingsFee;

  return composeFee({
    guaranteeType: gType,
    guaranteeSource: 'menu',
    baseFee,
    perPerson,
    menu,
    partySize,
    salesTaxPercent,
    checkoutProcessingFeePercent,
    tipChargePercent,
    restaurantSubdomain,
    pairingsFee,
    currency,
    isUSD,
  });
}

// ─── Slot guarantee computation ───────────────────────────────────────

interface SlotFeeParams {
  slot: ProcessedSlot;
  menu: TimeSlotMenu | undefined;
  partySize: number;
  kidsCount: number;
  settleUsd: boolean;
  salesTaxPercent: number;
  restaurantSubdomain?: string;
  checkoutProcessingFeePercent: number;
  tipChargePercent?: number;
  isForeign: boolean;
  selectedPairings: SelectedPairing[];
  pairingsInUsd: boolean;
  currency: string;
  isUSD: boolean;
}

function computeFeeFromSlot({
  slot,
  menu,
  partySize,
  kidsCount,
  settleUsd,
  salesTaxPercent,
  restaurantSubdomain,
  checkoutProcessingFeePercent,
  tipChargePercent,
  isForeign,
  selectedPairings,
  pairingsInUsd,
  currency,
  isUSD,
}: SlotFeeParams): FeeBreakdown {
  const gType = slot.type as 'deposit' | 'no_show';
  let unitFee: number;

  // Check foreign/local special rules via allFees
  if (isForeign && slot.allFees) {
    unitFee = gType === 'deposit' ? slot.allFees.deposit : slot.allFees.noShow;
    if (settleUsd) {
      unitFee = (gType === 'deposit' ? slot.allFees.depositUSD : slot.allFees.noShowUSD) || unitFee;
    }
  } else if (!isForeign && slot.allFees) {
    unitFee = gType === 'deposit' ? slot.allFees.deposit : slot.allFees.noShow;
    if (settleUsd) {
      unitFee = (gType === 'deposit' ? slot.allFees.depositUSD : slot.allFees.noShowUSD) || unitFee;
    }
  } else {
    unitFee = settleUsd ? (slot.feeUSD || slot.fee || 0) : (slot.fee ?? 0);
  }

  // Per-person at slot level
  const perPerson = slot.guaranteeType === 'per_person';
  let baseFee: number;

  if (perPerson) {
    const slotKidFee = settleUsd ? (slot.feeKidsUSD || slot.feeKids || 0) : (slot.feeKids ?? 0);

    if (slotKidFee > 0 && kidsCount > 0) {
      const adultsCount = Math.max(partySize - kidsCount, 0);
      baseFee = (unitFee * adultsCount) + (slotKidFee * kidsCount);
    } else {
      baseFee = unitFee * partySize;
    }
  } else {
    baseFee = unitFee;
  }

  // Add pairing guarantee
  const pairingsFee = calculatePairingsFee(selectedPairings, pairingsInUsd);
  baseFee += pairingsFee;

  return composeFee({
    guaranteeType: gType,
    guaranteeSource: 'slot',
    baseFee,
    perPerson,
    menu,
    partySize,
    salesTaxPercent,
    checkoutProcessingFeePercent,
    tipChargePercent,
    restaurantSubdomain,
    pairingsFee,
    currency,
    isUSD,
  });
}

// ─── Fee composition (gratuity, tax, service fee) ─────────────────────

interface ComposeFeeParams {
  guaranteeType: 'deposit' | 'no_show';
  guaranteeSource: 'menu' | 'slot' | 'specialRequest';
  baseFee: number;
  perPerson: boolean;
  menu: TimeSlotMenu | undefined;
  partySize: number;
  salesTaxPercent: number;
  checkoutProcessingFeePercent: number;
  tipChargePercent?: number;
  restaurantSubdomain?: string;
  pairingsFee: number;
  currency: string;
  isUSD: boolean;
}

function composeFee({
  guaranteeType,
  guaranteeSource,
  baseFee,
  perPerson,
  menu,
  partySize,
  salesTaxPercent,
  checkoutProcessingFeePercent,
  tipChargePercent,
  restaurantSubdomain,
  pairingsFee,
  currency,
  isUSD,
}: ComposeFeeParams): FeeBreakdown {
  // Gratuity (deposit only, when menu.chargeTip)
  let gratuity = 0;
  let gratuityPercent = 0;
  if (guaranteeType === 'deposit' && menu?.chargeTip) {
    gratuityPercent = tipChargePercent
      ?? (restaurantSubdomain === 'quintonil' ? QUINTONIL_GRATUITY_PERCENT : DEFAULT_GRATUITY_PERCENT);
    gratuity = baseFee * (gratuityPercent / 100);
  }

  // Menu tax (deposit only)
  let menuTax = 0;
  if (guaranteeType === 'deposit' && menu?.tax) {
    menuTax = menu.tax * (perPerson ? partySize : 1);
  }

  // Sales tax
  const effectiveSalesTax = salesTaxPercent || 0;
  const subtotalBeforeSalesTax = baseFee + gratuity + menuTax;
  const salesTax = subtotalBeforeSalesTax * (effectiveSalesTax / 100);

  // Service fee (deposit only)
  let serviceFee = 0;
  const serviceFeePercent = guaranteeType === 'deposit' ? (checkoutProcessingFeePercent || 0) : 0;
  if (serviceFeePercent > 0) {
    serviceFee = (subtotalBeforeSalesTax + salesTax) * (serviceFeePercent / 100);
  }

  const total = baseFee + gratuity + menuTax + salesTax + serviceFee;

  return {
    guaranteeType,
    guaranteeSource,
    baseFee,
    perPerson,
    gratuity,
    gratuityPercent,
    menuTax,
    salesTax,
    salesTaxPercent: effectiveSalesTax,
    serviceFee,
    serviceFeePercent,
    pairingsFee,
    total,
    hasBreakdown: gratuity > 0 || menuTax > 0 || salesTax > 0 || serviceFee > 0,
    currency,
    isUSD,
  };
}

// ─── Pairing fee helper ───────────────────────────────────────────────

/** Whether pairings are charged from priceUSD instead of price: under settleUsd, unless restaurantCurrency is 'USD'. */
export function pairingsPricedInUsd(settleUsd: boolean, restaurantCurrency: string | undefined): boolean {
  return settleUsd && restaurantCurrency !== 'USD';
}

export function calculatePairingsFee(pairings: SelectedPairing[], inUsd: boolean): number {
  let fee = 0;
  for (const p of pairings) {
    if (p.affectsGuarantee) {
      const unitPrice = inUsd ? (p.priceUSD ?? 0) : p.price;
      fee += unitPrice * p.quantity;
    }
  }
  return fee;
}
